/**
 * WHAT TO CALL AN UPLOAD.
 *
 * An upload has carried a source filename since the beginning, and a filename is not a name: it is
 * whatever the spreadsheet happened to be called, it repeats, and it cannot be corrected. Migration
 * 028 gives the upload a name of its own, editable and separate, and leaves the filename alone.
 *
 * So there are three things to say about an upload, in this order:
 *   1. the name the operator gave it,
 *   2. the file it came from, when there is no name — which is exactly what every surface showed
 *      before, so an upload nobody has named looks the way it always did,
 *   3. a dated label, when there is not even a filename (a run started from a pasted list).
 *
 * Nothing here invents a name on the server's behalf: the fallback is a LABEL, computed for
 * display, and `renameIssuanceBatch(null)` goes back to it rather than storing it.
 */

/** The name, trimmed, or null — an all-whitespace name is not a name. */
export function uploadName(upload: { name?: string | null }): string | null {
  const name = (upload.name ?? '').trim();
  return name.length > 0 ? name : null;
}

/** The filename, trimmed, or null. */
export function uploadFilename(upload: {
  filename?: string | null;
  sourceFilename?: string | null;
}): string | null {
  const file = (upload.filename ?? upload.sourceFilename ?? '').trim();
  return file.length > 0 ? file : null;
}

/**
 * What a list prints for an upload. Never empty, so a row cannot come out blank.
 *
 * `createdAt` is used only for the third case and only as a date, never as a time: an upload is
 * identified by its id, and a label that changes with the reader's clock would be a worse name
 * than «Adsız yükleme».
 */
export function uploadLabel(upload: {
  name?: string | null;
  filename?: string | null;
  sourceFilename?: string | null;
  createdAt?: string | null;
}): string {
  const named = uploadName(upload);
  if (named) return named;
  const file = uploadFilename(upload);
  if (file) return file;
  const day = (upload.createdAt ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? `Adsız yükleme · ${day}` : 'Adsız yükleme';
}

/**
 * The second line, when there is one: the file an upload's name is NOT.
 *
 * Null when the label already IS the filename, so the same text is never printed twice.
 */
export function uploadSubLabel(upload: {
  name?: string | null;
  filename?: string | null;
  sourceFilename?: string | null;
}): string | null {
  return uploadName(upload) ? uploadFilename(upload) : null;
}
